import { BaseInputProps } from '@components/InputFieldControlled/InputText.types';
import { Controller } from 'react-hook-form';
import { Box, FormLabel, InputAdornment, TextField, Typography, useTheme } from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import composeRules, { getValidationRules } from '@utils/composeRules';

type InputTimeProps = {} & BaseInputProps;

function applyTimeMask(raw: string): string {
    const digits = raw.replace(/\D/g, '').slice(0, 4);
    if (digits.length === 0) return '';

    let h1 = digits[0];
    let h2 = digits[1] ?? '';
    const m1 = digits[2] ?? '';
    const m2 = digits[3] ?? '';

    // Constrain first digit: 0-2
    if (parseInt(h1) > 2) h1 = '2';
    // Constrain second hour digit: if first is '2', limit to 0-3
    if (h2 && h1 === '2' && parseInt(h2) > 3) h2 = '3';
    // Constrain first minute digit: 0-5
    const m1Clamped = m1 && parseInt(m1) > 5 ? '5' : m1;

    const hours = h1 + h2;
    const minutes = m1Clamped + m2;

    if (digits.length <= 2) return hours;
    return `${hours}:${minutes}`;
}

const InputTime = (props: InputTimeProps) => {
    const theme = useTheme();
    const rules = composeRules(props.required, props.rules);

    return (
        <Box sx={{ width: '100%', ...props.containerStyle }}>
            <Controller
                control={props.control}
                name={props.name}
                defaultValue={props.defaultValue ?? null}
                rules={{
                    validate: getValidationRules(rules ?? {}),
                }}
                render={({ field: { ref, name, value, onChange }, fieldState: { error } }) => {
                    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                        const masked = applyTimeMask(e.target.value);
                        onChange(masked);
                        props.onChange?.(masked);
                    };

                    return (
                        <>
                            {props.label && (
                                <FormLabel htmlFor={name}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mb: 0.5,
                                            color: error
                                                ? theme.palette.errorRed.main
                                                : props.disabled || props.viewOnly
                                                  ? theme.palette.text.disabled
                                                  : theme.palette.text.primary,
                                        }}
                                    >
                                        {props.label}
                                    </Typography>
                                </FormLabel>
                            )}
                            <TextField
                                id={name}
                                inputRef={ref}
                                value={value ?? ''}
                                onChange={handleChange}
                                placeholder={props.placeholder ?? 'HH:MM'}
                                disabled={props.disabled}
                                error={!!error}
                                fullWidth
                                inputProps={{ maxLength: 5 }}
                                InputProps={{
                                    readOnly: props.viewOnly,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <AccessTimeOutlinedIcon
                                                fontSize="small"
                                                sx={{
                                                    color: error
                                                        ? theme.palette.errorRed.main
                                                        : theme.palette.action.active,
                                                }}
                                            />
                                        </InputAdornment>
                                    ),
                                    sx: props.inputStyle,
                                }}
                            />
                            {error?.message && (
                                <Typography variant="caption" sx={{ color: theme.palette.errorRed.main, mt: 0.5 }}>
                                    {error.message}
                                </Typography>
                            )}
                        </>
                    );
                }}
            />
        </Box>
    );
};

export default InputTime;
